import { useBlocker } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { useAuth } from '~/auth/auth-context'
import { Working } from '~/components/working'
import { reached, saidOf, useWork } from '~/components/use-work'
import { WeighInDayRow } from '~/components/weighin-day-row'
import { WeighInPhotoStrip } from '~/components/weighin-photos-panel'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { PanelHeader } from '~/components/panel-header'
import { ChevronDownIcon } from '~/components/icons'
import { useToast } from '~/components/toast-context'
import { UnitInput } from '~/components/unit-input'
import { useApi } from '~/data/data-context'
import {
  MEASUREMENTS,
  MEASUREMENT_LABELS,
  MEASUREMENT_UNITS,
  SLEEP_MEASUREMENTS,
  clockSaid,
  minutesOfClock,
  measurementDisplay,
  measurementStored,
  todayKey,
  type WeighIn,
  type WeighInPhoto,
  type Measurement,
} from '~/data/weighins'
import type { Units } from '~/data/types'
import { readingOf } from '~/lib/metric-text'
import { dayStart, formatDayLabel } from '~/lib/dates'
import { displayToKg, kgToDisplay } from '~/lib/units'

/**
 * A day's weigh-in, morphed into the corner card: the weigh-in front and centre, and the tape
 * split the way settings asked — the measurements taken routinely in front, the rest behind the
 * fold. Seeded from today's weigh-in, and the way out guarded like feedback's: every exit is
 * a navigation, and it asks while anything typed here is unsaved.
 */
export function WeighInWeightPanel({
  day,
  weighIn,
  opensOn = null,
  onSaved,
  onShowPhoto,
  onRemovePhoto,
  onBack,
}: {
  /** The day being logged, as `YYYY-MM-DD`. The panel is remounted when it changes. */
  day: string
  /** What that day already holds, seeding the form. */
  weighIn: WeighIn | undefined
  /**
   * The reading to open on, where the panel was opened for one — a pill on a day's heading
   * carries the reading it names. That field takes the cursor instead of the weight, and the
   * fold opens first where the reading is behind it. Null opens on the weight, as ever.
   */
  opensOn?: string | null
  onSaved: (saved: WeighIn) => void
  onShowPhoto: (photo: WeighInPhoto) => void
  onRemovePhoto: (photo: WeighInPhoto) => void
  onBack: () => void
}) {
  const api = useApi()
  const toast = useToast()
  const { units, settings } = useAuth()

  const front = MEASUREMENTS.filter((name) => settings.weighInRoutine.includes(name))
  const folded = MEASUREMENTS.filter((name) => !settings.weighInRoutine.includes(name))

  const seededWeight = weighIn?.weightKg == null ? '' : String(kgToDisplay(weighIn.weightKg, units))
  const seededTape = Object.fromEntries(
    MEASUREMENTS.map((name) => {
      const kept = weighIn?.measurements[name]
      if (kept == null) {
        return [name, '']
      }
      // A time of day is written as a clock, not as the number of minutes behind it.
      return [name, isSleep(name) ? clockSaid(kept) : String(measurementDisplay(kept))]
    }),
  ) as Record<Measurement, string>

  const [weight, setWeight] = useState(seededWeight)
  const [tape, setTape] = useState(seededTape)
  /*
   * Which field the cursor lands in. The weight unless the panel was opened for a reading —
   * pressing "Waist 84" on a day's heading means wanting to change the waist, and arriving at
   * the top of the form to scroll for it is arriving in the wrong place.
   */
  const opening: Reading =
    opensOn !== null && (READINGS as string[]).includes(opensOn) ? (opensOn as Reading) : 'weight'

  // The fold starts open on a day the tape behind it has already been out — or on one whose
  // reading is behind it, which is a field that would otherwise be opened on and not shown.
  const [taping, setTaping] = useState(
    () =>
      MEASUREMENTS.some(
        (name) => !settings.weighInRoutine.includes(name) && weighIn?.measurements[name] != null,
      ) ||
      (opening !== 'weight' && !settings.weighInRoutine.includes(opening)),
  )
  const saving = useWork()
  /** Which readings a save is about to take off the day, or empty while nothing asks. */
  const [removing, setRemoving] = useState<Reading[]>([])

  const dirty =
    weight !== seededWeight || MEASUREMENTS.some((name) => tape[name] !== seededTape[name])

  // Every way out is a navigation — Escape, Back, a click elsewhere, the browser's back,
  // and moving to another day — so the guard sits on the navigation, as feedback's does.
  const holding = useRef(false)
  useEffect(() => {
    holding.current = dirty
  }, [dirty])

  const leaving = useBlocker({
    shouldBlockFn: ({ next }) =>
      holding.current &&
      (next.search.dialog !== 'weighin-weight' || (next.search.day ?? todayKey()) !== day),
    enableBeforeUnload: false,
    withResolver: true,
  })

  const put = async (weightKg: number | null, typed: Record<Measurement, string>) => {
    return api.putWeighIn(day, {
      weightKg,
      measurements: Object.fromEntries(
        MEASUREMENTS.map((name) => {
          // Midnight is nought minutes past it, which is a reading like any other — so these
          // are read apart from the rest, whose nought means nothing was typed.
          if (isSleep(name)) {
            return [name, minutesOfClock(typed[name])]
          }
          const read = Number(typed[name])
          return [
            name,
            typed[name].trim() !== '' && Number.isFinite(read) && read > 0
              ? measurementStored(read)
              : null,
          ]
        }),
      ),
    })
  }

  /**
   * The day, saved. Refused where the server was not reached — see [reached] — which the
   * confirmation to remove a reading says in itself, and a press on the page says as a toast.
   */
  const save = () =>
    saving.run(async () => {
      const weightNumber = Number(weight)
      const saved = await reached(() =>
        put(
          weight.trim() !== '' && Number.isFinite(weightNumber) && weightNumber > 0
            ? displayToKg(weightNumber, units)
            : null,
          tape,
        ),
      )
      onSaved(saved)
      toast('Checked in.')
      holding.current = false
      onBack()
    })

  /**
   * Saving is also how a reading is taken off a day: the field it was in is emptied and the day
   * goes up without it. That is a loss like any other, so it is said beside the save while the
   * field stands empty, and asked about before it happens rather than being the quiet half of
   * pressing save. What is not emptied goes up as it stands in the form, so removing one reading
   * cannot take typing beside it along.
   */
  const gone = READINGS.filter(
    (reading) =>
      dayHolds(weighIn, reading) != null &&
      entered(reading === 'weight' ? weight : tape[reading]) == null,
  )

  const onSubmit = () => {
    if (gone.length === 0) {
      // Pressed on the page, a failure is said as a toast.
      save().catch((error: unknown) => {
        toast(saidOf(error))
      })
    } else {
      setRemoving(gone)
    }
  }

  return (
    <>
      <PanelHeader title="Weigh-in" close onBack={onBack} />
      <WeighInDayRow day={day} dialog="weighin-weight" />
      {/*
        A form, so Enter out of any of these fields saves the day the way pressing Save does —
        a weigh-in is one number typed one-handed, and reaching for the mouse to put it away is
        the slowest part of it. Everything here that is not the save is a `type="button"`, so
        nothing else answers the Enter.
      */}
      <form
        className="w-80 space-y-3 px-4 pb-4"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <UnitInput
          value={weight}
          unit={units === 'lb' ? 'lb' : 'kg'}
          label="Body weight"
          focused={opening === 'weight'}
          size="text-xl"
          onValue={setWeight}
        />

        {front.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {front.map((name) => (
              <MeasurementField
                key={name}
                name={name}
                tape={tape}
                setTape={setTape}
                focused={opening === name}
              />
            ))}
          </div>
        )}

        {folded.length > 0 && (
          <>
            <button
              type="button"
              aria-expanded={taping}
              className="flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink"
              onClick={() => {
                setTaping((held) => !held)
              }}
            >
              <ChevronDownIcon
                aria-hidden
                className={`size-5 transition-transform ${taping ? 'rotate-180' : ''}`}
              />
              More measurements
            </button>

            {taping && (
              <div className="grid grid-cols-2 gap-3">
                {folded.map((name) => (
                  <MeasurementField
                    key={name}
                    name={name}
                    tape={tape}
                    setTape={setTape}
                    focused={opening === name}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* The day's shots, in the weigh-in they are part of. */}
        <WeighInPhotoStrip
          day={day}
          photos={weighIn?.photos ?? []}
          onShow={onShowPhoto}
          onRemove={onRemovePhoto}
        />

        <div className="flex items-center gap-3 pt-1">
          {gone.length > 0 && (
            <p className="text-sm text-danger">Saving removes {labelsOf(gone)}</p>
          )}
          <span className="flex-1" />
          <button
            type="submit"
            aria-busy={saving.working || undefined}
            className="relative rounded-full bg-accent px-5 py-2 text-sm font-medium text-on-accent disabled:opacity-50"
          >
            <Working shown={saving.shown}>Save</Working>
          </button>
        </div>
      </form>

      {leaving.status === 'blocked' && (
        <ConfirmDialog
          title="Discard your measurements?"
          body="What you have entered today is not saved."
          confirm="Discard"
          onConfirm={() => {
            holding.current = false
            leaving.proceed()
          }}
          onDismiss={leaving.reset}
        />
      )}

      {removing.length > 0 && weighIn != null && (
        <ConfirmDialog
          title={removalTitle(removing)}
          body={`The ${namesOf(removing, weighIn, units)} logged ${
            day === todayKey() ? 'today' : `on ${formatDayLabel(dayStart(day))}`
          } ${removing.length === 1 ? 'goes' : 'go'}. The rest of the day stays.`}
          confirm="Remove"
          onConfirm={async () => {
            await save()
            setRemoving([])
          }}
          onDismiss={() => {
            setRemoving([])
          }}
        />
      )}
    </>
  )
}

/** One measurement, labelled by its name and masked in whatever it is read in. */
function MeasurementField({
  name,
  tape,
  setTape,
  focused = false,
}: {
  name: Measurement
  tape: Record<Measurement, string>
  setTape: React.Dispatch<React.SetStateAction<Record<Measurement, string>>>
  /** Whether this is the field the panel was opened on, and so takes the cursor. */
  focused?: boolean
}) {
  if (isSleep(name)) {
    /*
     * A time of day rather than a number: the browser's own time control, which is what every
     * other time in the app is set on. Nobody types a clock on a numeric keypad.
     */
    return (
      <label className="flex items-center justify-between rounded-xl px-3 py-2.5">
        {MEASUREMENT_LABELS[name]}
        <input
          type="time"
          aria-label={MEASUREMENT_LABELS[name]}
          value={tape[name]}
          ref={(field) => {
            // The panel was opened on this field, so the cursor belongs in it — the same as
            // every other reading here, which take it through the field they are drawn with.
            if (focused) {
              field?.focus()
            }
          }}
          onChange={(event) => {
            setTape((held) => ({ ...held, [name]: event.target.value }))
          }}
          className="rounded-lg bg-surface px-2 py-1.5 text-sm tabular-nums outline-none ring-1 ring-white/10 focus:ring-2 focus:ring-accent"
        />
      </label>
    )
  }
  return (
    <UnitInput
      value={tape[name]}
      unit={MEASUREMENT_UNITS[name]}
      label={MEASUREMENT_LABELS[name]}
      focused={focused}
      onValue={(next) => {
        setTape((held) => ({ ...held, [name]: next }))
      }}
    />
  )
}

/** Whether a reading is one of the two clock times a night is known by. */
function isSleep(name: Measurement): boolean {
  return (SLEEP_MEASUREMENTS as readonly string[]).includes(name)
}

/** What a weigh-in records, one field apiece: the body weight, and each of the measurements. */
type Reading = 'weight' | Measurement

const READINGS: Reading[] = ['weight', ...MEASUREMENTS]

/** What the day holds for a reading, as the weigh-in keeps it, or null where it holds none. */
function dayHolds(weighIn: WeighIn | undefined, reading: Reading): number | null {
  if (weighIn == null) return null
  return (reading === 'weight' ? weighIn.weightKg : weighIn.measurements[reading]) ?? null
}

/** What a field holds, or null where nothing readable was typed into it. */
function entered(typed: string): number | null {
  const read = Number(typed)
  return typed.trim() !== '' && Number.isFinite(read) && read > 0 ? read : null
}

/** What the question asks about: the one reading going, or the several of them. */
function removalTitle(readings: Reading[]): string {
  const [only] = readings
  return readings.length === 1 && only != null
    ? `Remove this ${readingName(only)}?`
    : 'Remove these readings?'
}

/** The fields going, named as they are labelled: "Body weight, Neck and Chest". */
function labelsOf(readings: Reading[]): string {
  const said = readings.map((reading) =>
    reading === 'weight' ? 'Body weight' : MEASUREMENT_LABELS[reading],
  )
  if (said.length < 2) return said[0] ?? ''
  return `${said.slice(0, -1).join(', ')} and ${said[said.length - 1]}`
}

/** What a reading is called where it is being asked about: a weight is one, a girth has one. */
function readingName(reading: Reading): string {
  return reading === 'weight' ? 'weight' : `${MEASUREMENT_LABELS[reading].toLowerCase()} reading`
}

/** The readings themselves, as the day holds them, listed the way a sentence would list them. */
function namesOf(readings: Reading[], weighIn: WeighIn, units: Units): string {
  const said = readings.flatMap((reading) => {
    const value = dayHolds(weighIn, reading)
    if (value == null) return []
    const read = readingOf(reading === 'weight' ? 'scale' : reading, value, units)
    return [reading === 'weight' ? read : `${read} ${MEASUREMENT_LABELS[reading].toLowerCase()}`]
  })
  if (said.length < 2) return said[0] ?? ''
  return `${said.slice(0, -1).join(', ')} and ${said[said.length - 1]}`
}
